import { useInventory } from '@gozoki/api';
import { Product } from '@gozoki/api-types';
import { useMemo } from 'react';

export interface InventoryProductRow extends Record<string, string | number> {
    id: number;
    theoricalCount: number;
    reapproNeeds: number;
    inventoryCount: number;
    unscannedCount: number;
    delta: number;
    reference: string;
    label: string;
    price: number;
}

/**
 * Custom hook to calculate inventory product rows from inventory detail data.
 * Combines incoming stock, current stock, and unscannable products into a unified view.
 * Also merges reappro needs data to show expected deliveries.
 */
export const useInventoryProductRows = (
    inventoryDetail: NonNullable<ReturnType<typeof useInventory>['data']> | undefined,
    reapproNeedsData?: Record<string, { count: number; product: Product }> | null
): InventoryProductRow[] => {
    return useMemo(() => {
        if (!inventoryDetail?.incomingStock) return [];
        const incomingProductsAcc: InventoryProductRow[] = [];

        const incoming = inventoryDetail?.incomingStock?.reduce((acc, article) => {
            const product = acc.find((p) => p.id === article.product.id);

            if (product) {
                return acc.map((p) => {
                    if (p.id === article.product.id) {
                        p.inventoryCount += 1;
                        p.delta += 1;
                    }
                    return p;
                });
            }
            return acc.concat({
                id: article.product.id,
                inventoryCount: 1,
                theoricalCount: 0,
                reapproNeeds: 0,
                unscannedCount: 0,
                delta: +1,
                label: article.product.label,
                price: article.product.price,
                reference: article.product.reference,
            });
        }, incomingProductsAcc);

        const incomingCurrent = inventoryDetail?.currentStock
            ? inventoryDetail?.currentStock
                  ?.filter((article) => !article.isExpired)
                  .reduce((acc, article) => {
                      const product = acc.find((p) => p.id === article.product.id);
                      if (product) {
                          return acc.map((p) => {
                              if (p.id === article.product.id) {
                                  p.theoricalCount += 1;
                                  p.delta -= 1;
                              }
                              return p;
                          });
                      }
                      return acc.concat({
                          id: article.product.id,
                          inventoryCount: 0,
                          theoricalCount: 1,
                          reapproNeeds: 0,
                          unscannedCount: 0,
                          delta: -1,
                          label: article.product.label,
                          price: article.product.price,
                          reference: article.product.reference,
                      });
                  }, incoming)
            : incoming;

        const incomingOutgoingUnscanned = inventoryDetail?.unScannableProducts?.unScannableProducts
            ? inventoryDetail.unScannableProducts.unScannableProducts.reduce(
                  (acc, unScannableProduct) => {
                      return acc.map((p) => {
                          if (p.reference === unScannableProduct.productReference) {
                              p.unscannedCount = unScannableProduct.count;
                              p.delta += p.unscannedCount;
                          }
                          return p;
                      });
                  },
                  incomingCurrent
              )
            : incomingCurrent;

        // Merge reappro needs data and recalculate final delta
        const finalRows = reapproNeedsData
            ? incomingOutgoingUnscanned.map((p) => {
                  const reapproData = reapproNeedsData[p.reference];
                  const reapproNeeds = reapproData?.count || 0;
                  return {
                      ...p,
                      reapproNeeds,
                  };
              })
            : incomingOutgoingUnscanned;

        // Recalculate delta as: (scanned total) - (theoretical after delivery)
        return finalRows.map((p) => ({
            ...p,
            delta: p.inventoryCount + p.unscannedCount - (p.theoricalCount + p.reapproNeeds),
        }));
    }, [inventoryDetail, reapproNeedsData]);
};
